// Api
import LogisticsApi from '@/api/LogisticsApi';
import SettingsApi from '@/api/SettingsApi';

// Utils
import ErrorUtil from '@/utils/ErrorUtil';

const actionTypes = {
    get: 'get',
    update: 'update',
    updateMailLocators: 'updateMailLocators',
    reset: 'reset',
};
const mutationTypes = {
    SET_HAS_FETCHED_SETTINGS: 'SET_HAS_FETCHED_SETTINGS',
    SET_IN_FLIGHT: 'SET_IN_FLIGHT',

    SET_DYNAMIC_STORAGE: 'SET_DYNAMIC_STORAGE',
    SET_DYNAMIC_STORAGE_ENABLED: 'SET_DYNAMIC_STORAGE_ENABLED',
    SET_DYNAMIC_STORAGE_DRAWERS: 'SET_DYNAMIC_STORAGE_DRAWERS',
    SET_DYNAMIC_STORAGE_FOLDERS: 'SET_DYNAMIC_STORAGE_FOLDERS',

    SET_REQUIREMENTS: 'SET_REQUIREMENTS',
    SET_REQUIREMENT_PHOTO_PICKUP: 'SET_REQUIREMENT_PHOTO_PICKUP',
    SET_REQUIREMENT_PHOTO_DELIVERY: 'SET_REQUIREMENT_PHOTO_DELIVERY',

    SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
    SET_NOTIFICATION_RECEIVE: 'SET_NOTIFICATION_RECEIVE',
    SET_NOTIFICATION_REMINDER: 'SET_NOTIFICATION_REMINDER',
};

export default {
    namespaced: true,
    state: {
        hasFetchedSettings: false,
        inFlight: false,
        dynamicStorage: {
            enabled: false,
            drawers: 0,
            folders: 0,
        },
        requirements: {
            pickupPhoto: false,
            deliveryPhoto: false,
        },
        notifications: {
            receive: false,
            reminder: false,
        },
    },
    getters: {},
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_DYNAMIC_STORAGE, {
                enabled: false,
                drawers: 0,
                folders: 0,
            } );
            commit( mutationTypes.SET_REQUIREMENTS, {
                pickupPhoto: false,
                deliveryPhoto: false,
            } );
            commit( mutationTypes.SET_NOTIFICATIONS, {
                receive: false,
                reminder: false,
            } );
        },

        // BL-752 - Users don't want settings cached, change default to never cache
        async get( context, { force = true } = {} ) {
            if ( context.state.hasFetchedSettings && !force ) {
                return;
            }

            context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: true } );
            try {
                const { data } = await SettingsApi.getMailSettings( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                context.commit( mutationTypes.SET_DYNAMIC_STORAGE, {
                    enabled: data.mailSettings.useDynamicMailStorage,
                    drawers: data.mailSettings.dynamicDrawers,
                    folders: data.mailSettings.dynamicFolders,
                } );
                context.commit( mutationTypes.SET_REQUIREMENTS, {
                    pickupPhoto: data.mailSettings.pickupPhotoRequired,
                    deliveryPhoto: data.mailSettings.deliveryPhotoRequired,
                } );
                context.commit( mutationTypes.SET_NOTIFICATIONS, {
                    receive: data.mailSettings.sendReceiveEmailNotification,
                    reminder: data.mailSettings.sendReminderEmailNotification,
                } );
                context.commit( mutationTypes.SET_HAS_FETCHED_SETTINGS, {
                    boolean: true,
                } );
            } catch ( error ) {
                await context.dispatch( 'snackbar', {
                    message: `An error occurred while getting Mail Settings: ${ ErrorUtil.getMessageFromError( error ) }`,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                throw new Error( ErrorUtil.getMessageFromError( error ) );
            } finally {
                context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: false } );
            }
        },
        async update( context, settings ) {
            const settingsToSave = {
                pickupPhotoRequired: context.state.requirements.pickupPhoto || false,
                deliveryPhotoRequired: context.state.requirements.deliveryPhoto || false,
                useDynamicMailStorage: context.state.dynamicStorage.enabled || false,
                dynamicDrawers: context.state.dynamicStorage.drawers || 0,
                dynamicFolders: context.state.dynamicStorage.folders || 0,
                sendReceiveEmailNotification: context.state.notifications.receive || false,
                sendReminderEmailNotification: context.state.notifications.reminder || false,
                ...settings,
            };

            context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: true } );
            try {
                await SettingsApi.saveMailSettings( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    ...settingsToSave,
                } );

                await context.dispatch( 'snackbar', {
                    message: 'Successfully updated Mail Settings!',
                    type: 'success',
                    duration: 3000,
                }, { root: true } );

                await context.dispatch( actionTypes.get );
            } catch ( error ) {
                const message = `An error occurred while updating Mail Settings: ${ ErrorUtil.getMessageFromError( error ) }`;
                await context.dispatch( 'snackbar', {
                    message,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                throw new Error( ErrorUtil.getMessageFromError( error ) );
            } finally {
                context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: false } );
            }
        },
        async [ actionTypes.updateMailLocators ]( context, { drawers, folders } ) {
            context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: true } );
            try {
                await LogisticsApi.createMailLocators( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    numberOfDrawers: drawers,
                    numberOfFoldersPerDrawer: folders,
                } );
                context.commit( mutationTypes.SET_DYNAMIC_STORAGE_DRAWERS, { dynamicDrawers: drawers } );
                context.commit( mutationTypes.SET_DYNAMIC_STORAGE_FOLDERS, { dynamicFolders: folders } );
                await context.dispatch( actionTypes.update );
            } catch ( error ) {
                throw new Error( ErrorUtil.getMessageFromError( error ) );
            } finally {
                context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: false } );
            }
        },
    },
    mutations: {
        [ mutationTypes.SET_HAS_FETCHED_SETTINGS ]( state, { boolean } ) {
            state.hasFetchedSettings = boolean;
        },
        [ mutationTypes.SET_IN_FLIGHT ]( state, { inFlight } ) {
            state.inFlight = inFlight;
        },

        [ mutationTypes.SET_DYNAMIC_STORAGE ]( state, {
            enabled,
            drawers,
            folders,
        } ) {
            state.dynamicStorage.enabled = enabled || false;
            state.dynamicStorage.drawers = drawers || 0;
            state.dynamicStorage.folders = folders || 0;
        },
        SET_DYNAMIC_STORAGE_ENABLED( state, { boolean } ) {
            state.dynamicStorage.enabled = boolean || false;
        },
        [ mutationTypes.SET_DYNAMIC_STORAGE_DRAWERS ]( state, { dynamicDrawers } ) {
            state.dynamicStorage.drawers = dynamicDrawers || 0;
        },
        [ mutationTypes.SET_DYNAMIC_STORAGE_FOLDERS ]( state, { dynamicFolders } ) {
            state.dynamicStorage.folders = dynamicFolders || 0;
        },

        [ mutationTypes.SET_REQUIREMENTS ]( state, {
            pickupPhoto,
            deliveryPhoto,
        } ) {
            state.requirements.pickupPhoto = pickupPhoto || false;
            state.requirements.deliveryPhoto = deliveryPhoto || false;
        },
        [ mutationTypes.SET_REQUIREMENT_PHOTO_DELIVERY ]( state, { boolean } ) {
            state.requirements.deliveryPhoto = boolean || false;
        },
        [ mutationTypes.SET_REQUIREMENT_PHOTO_PICKUP ]( state, { boolean } ) {
            state.requirements.pickupPhoto = boolean || false;
        },

        [ mutationTypes.SET_NOTIFICATIONS ]( state, { receive, reminder } ) {
            state.notifications.receive = receive;
            state.notifications.reminder = reminder;
        },
        [ mutationTypes.SET_NOTIFICATION_RECEIVE ]( state, { boolean } ) {
            state.notifications.receive = boolean;
        },
        [ mutationTypes.SET_NOTIFICATION_REMINDER ]( state, { boolean } ) {
            state.notifications.reminder = boolean;
        },
    },
};
